/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { EasOsdCountry } from "./EasOsdCountry";
import { EasOsdPosition } from "./EasOsdPosition";
import { EasOsdTemplateConfiguration } from "./EasOsdTemplateConfiguration";

/**
 * Singleton that selects the Emergency Alert System (EAS) OSD template
 * configuration for a given country. Selection is data-driven via a per-country
 * registry with a safe default fallback for unconfigured countries.
 *
 * Access via {@link EasOsdTemplateSelector.getInstance}.
 */
export class EasOsdTemplateSelector {
  private static instance: EasOsdTemplateSelector | null = null;

  /** Per-country template configuration registry. */
  private readonly configurations: Readonly<
    Record<EasOsdCountry, EasOsdTemplateConfiguration>
  > = {
    [EasOsdCountry.UnitedStates]: {
      position: EasOsdPosition.Top
    },
    [EasOsdCountry.Canada]: {
      position: EasOsdPosition.FullScreen
    },
    [EasOsdCountry.ElSalvador]: {
      position: EasOsdPosition.Bottom
    },
    [EasOsdCountry.Honduras]: {
      position: EasOsdPosition.Bottom
    }
  };

  /**
   * Fallback configuration for countries without an explicit entry. Mirrors the
   * United States template (top of screen), which is the product default.
   */
  private readonly defaultConfiguration: EasOsdTemplateConfiguration = {
    position: EasOsdPosition.Top
  };

  private constructor() {}

  /** Returns the shared {@link EasOsdTemplateSelector} instance. */
  public static getInstance(): EasOsdTemplateSelector {
    if (EasOsdTemplateSelector.instance === null) {
      EasOsdTemplateSelector.instance = new EasOsdTemplateSelector();
    }

    return EasOsdTemplateSelector.instance;
  }

  /**
   * Returns the EAS OSD template configuration for the supplied country.
   * Unknown or unsupported countries resolve to the default configuration.
   *
   * @param country `easConfiguration.easDisplayMode` value (e.g. "us",
   *   "canada", "elSalvador", "honduras"); case-insensitive.
   * @returns A copy of the resolved template configuration.
   */
  public selectByCountry(country: string): EasOsdTemplateConfiguration {
    const normalized = this.normalizeCountry(country);
    const configuration =
      normalized !== null
        ? this.configurations[normalized]
        : this.defaultConfiguration;

    return { ...configuration };
  }

  /**
   * Reports whether an explicit EAS OSD configuration exists for the country.
   *
   * @param country `easConfiguration.easDisplayMode` value (e.g. "us",
   *   "canada", "elSalvador", "honduras"); case-insensitive.
   */
  public isSupportedCountry(country: string): boolean {
    return this.normalizeCountry(country) !== null;
  }

  /**
   * Normalises an input country string to a known {@link EasOsdCountry}
   * (United States or Canada), or `null` when the country falls back to the
   * "Latam" configuration. Matching is case-insensitive so both the raw
   * `easDisplayMode` value and any cased variant resolve correctly.
   */
  private normalizeCountry(country: string): EasOsdCountry | null {
    if (typeof country !== "string" || country.trim().length === 0) {
      return null;
    }

    const target = country.trim().toLowerCase();
    const supported = Object.values(EasOsdCountry);

    for (const value of supported) {
      if (value.toLowerCase() === target) {
        return value;
      }
    }

    return null;
  }
}
